// src/modules/operaciones/inventarios/dashboard/TablaDetalle.js
//
// Pestaña "Datos": la tabla original del archivo, con
//   - selector de grupos de columnas (Artículo, una bodega, Total merma, Venta...)
//   - buscador de texto
//   - orden por columna (clic en el encabezado: asc → desc → sin orden)
//   - paginación (25 / 50 / 100 filas)
//   - totales de las filas filtradas (y el "Total general" del archivo cuando aplica)
//   - exportar CSV de lo que se ve (filtrado, ordenado y con las columnas elegidas)
//
// USO
//   <TablaDetalle analisis={d.analisis} />
//
// Espera analisis.datos = { columnas, filas, filaTotal, grupos, totalFilas } (salida de analizarMermas).
//   - `filas` ya vienen con los filtros del dashboard aplicados (bodega, fabricante, etc.).
//   - Las columnas del grupo "Artículo" (identificacion) siempre se muestran, para no perder el contexto.
//
// CSV: separador ";", decimales con coma y BOM UTF-8, para que Excel en español lo abra directo.
// Los números se exportan sin formato ($, puntos de miles), listos para calcular.

import React, { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Download, Search, Table2, X } from "lucide-react";

import { fmtFecha, fmtMoneda, fmtNumero } from "../utils/informesComun";
import { normalizarNumero, normalizarTexto } from "../utils/detectorColumnas";

/* ========================================================================== */
/* Constantes y utilidades                                                    */
/* ========================================================================== */

const ID_IDENTIFICACION = "identificacion";
const GRUPOS_POR_DEFECTO = [ID_IDENTIFICACION, "total-merma", "venta"];
const TAMANOS_PAGINA = [25, 50, 100];

const esNumerica = (col) => col?.tipo === "numero" || col?.tipo === "moneda";
const vacio = (v) => v === null || v === undefined || v === "";

function valorNumerico(v) {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  return normalizarNumero(v);
}

// Texto y estilo de una celda según el tipo de la columna
function formatearCelda(v, col) {
  if (vacio(v)) return { texto: "", clase: "" };
  if (col?.tipo === "codigo") return { texto: String(v), clase: "text-gray-500 font-mono" };

  if (esNumerica(col) || typeof v === "number") {
    const n = valorNumerico(v);
    if (n === null) return { texto: String(v), clase: "" };
    if (n === 0) return { texto: "–", clase: "text-gray-300" };
    const texto = col?.tipo === "moneda" ? fmtMoneda(n, { decimales: Number.isInteger(n) ? 0 : 2 }) : fmtNumero(n);
    return { texto, clase: n < 0 ? "text-red-600" : "text-gray-800" };
  }
  if (col?.tipo === "fecha") return { texto: fmtFecha(v), clase: "" };
  return { texto: String(v), clase: "text-gray-800" };
}

// Encabezado en dos líneas: niveles superiores (pequeño) y último nivel (negrita)
function partesEncabezado(col) {
  const partes = Array.isArray(col.encabezado) && col.encabezado.length > 0 ? col.encabezado : [col.nombre];
  return { arriba: partes.slice(0, -1).join(" › "), abajo: partes[partes.length - 1] };
}

/* ========================================================================== */
/* CSV                                                                        */
/* ========================================================================== */

function celdaCsv(v, col) {
  if (vacio(v)) return "";
  let s;
  if (col?.tipo === "codigo") {
    s = String(v);
  } else {
    const n = esNumerica(col) || typeof v === "number" ? valorNumerico(v) : null;
    if (n !== null) return String(n).replace(".", ",");
    s = String(v);
  }
  // Evita que Excel interprete texto como fórmula
  if (/^[=+@\t\r-]/.test(s)) s = `'${s}`;
  return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function descargarCsv(nombreArchivo, texto) {
  const blob = new Blob([`\uFEFF${texto}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombreArchivo;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ========================================================================== */
/* Componente                                                                 */
/* ========================================================================== */

function SinDatos({ mensaje = "No hay datos para mostrar." }) {
  return (
    <div className="flex flex-col items-center justify-center text-center bg-white border border-dashed border-gray-300 rounded-xl py-16 px-4">
      <Table2 className="w-10 h-10 text-gray-300 mb-3" aria-hidden="true" />
      <p className="text-sm font-medium text-gray-700">{mensaje}</p>
    </div>
  );
}

export function TablaDetalle({ analisis }) {
  const datos = analisis?.datos;
  const columnas = useMemo(() => datos?.columnas || [], [datos]);
  const filas = useMemo(() => datos?.filas || [], [datos]);
  const grupos = useMemo(() => datos?.grupos || [], [datos]);
  const filaTotal = datos?.filaTotal || null;

  const [gruposActivos, setGruposActivos] = useState(() => new Set());
  const [busqueda, setBusqueda] = useState("");
  const [orden, setOrden] = useState(null); // { id, dir: "asc" | "desc" } | null
  const [tamano, setTamano] = useState(50);
  const [pagina, setPagina] = useState(1);

  /* ---------- Grupos de columnas ---------- */
  const firmaGrupos = grupos.map((g) => g.id).join("|");

  const gruposBasicos = useMemo(() => {
    const ids = new Set(grupos.map((g) => g.id));
    const base = GRUPOS_POR_DEFECTO.filter((id) => ids.has(id));
    return new Set(base.length > 0 ? base : grupos.slice(0, 1).map((g) => g.id));
  }, [grupos]);

  // Si cambian los grupos (otro archivo o mapeo distinto), vuelve a la selección básica
  useEffect(() => {
    setGruposActivos(new Set(gruposBasicos));
    setOrden(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmaGrupos]);

  const alternarGrupo = (id) => {
    if (id === ID_IDENTIFICACION) return; // siempre visible
    setGruposActivos((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });
  };

  const columnasVisibles = useMemo(() => {
    const porId = new Map(columnas.map((c) => [c.id, c]));
    const vistos = new Set();
    const lista = [];
    grupos.forEach((g) => {
      if (g.id !== ID_IDENTIFICACION && !gruposActivos.has(g.id)) return;
      g.columnas.forEach((id) => {
        if (vistos.has(id) || !porId.has(id)) return;
        vistos.add(id);
        lista.push(porId.get(id));
      });
    });
    return lista;
  }, [columnas, grupos, gruposActivos]);

  const firmaColumnas = columnasVisibles.map((c) => c.id).join("|");

  /* ---------- Búsqueda ---------- */
  const indiceBusqueda = useMemo(
    () =>
      filas.map((f) =>
        normalizarTexto(columnasVisibles.map((c) => (vacio(f[c.id]) ? "" : String(f[c.id]))).join(" "), { minusculas: true })
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filas, firmaColumnas]
  );

  const filasFiltradas = useMemo(() => {
    const q = normalizarTexto(busqueda, { minusculas: true });
    if (!q) return filas;
    return filas.filter((_, i) => indiceBusqueda[i].includes(q));
  }, [filas, indiceBusqueda, busqueda]);

  /* ---------- Orden ---------- */
  const filasOrdenadas = useMemo(() => {
    if (!orden) return filasFiltradas;
    const col = columnas.find((c) => c.id === orden.id);
    if (!col) return filasFiltradas;
    const factor = orden.dir === "asc" ? 1 : -1;
    const numerica = esNumerica(col);
    return [...filasFiltradas].sort((a, b) => {
      const va = a[col.id];
      const vb = b[col.id];
      if (vacio(va)) return 1; // los vacíos siempre al final
      if (vacio(vb)) return -1;
      if (numerica) {
        const na = valorNumerico(va) ?? 0;
        const nb = valorNumerico(vb) ?? 0;
        return factor * (na - nb);
      }
      return factor * String(va).localeCompare(String(vb), "es", { numeric: true, sensitivity: "base" });
    });
  }, [filasFiltradas, orden, columnas]);

  const cambiarOrden = (col) =>
    setOrden((prev) => {
      const primera = esNumerica(col) ? "desc" : "asc";
      if (!prev || prev.id !== col.id) return { id: col.id, dir: primera };
      if (prev.dir === primera) return { id: col.id, dir: primera === "asc" ? "desc" : "asc" };
      return null; // tercer clic: sin orden
    });

  /* ---------- Paginación ---------- */
  useEffect(() => {
    setPagina(1);
  }, [busqueda, orden, firmaColumnas, tamano, filas]);

  const total = filasOrdenadas.length;
  const totalPaginas = Math.max(Math.ceil(total / tamano), 1);
  const paginaActual = Math.min(pagina, totalPaginas);
  const desde = (paginaActual - 1) * tamano;
  const filasPagina = filasOrdenadas.slice(desde, desde + tamano);

  /* ---------- Totales de las filas filtradas ---------- */
  const totales = useMemo(() => {
    const t = {};
    columnasVisibles.forEach((c) => {
      if (!esNumerica(c)) return;
      t[c.id] = filasFiltradas.reduce((s, f) => s + (valorNumerico(f[c.id]) ?? 0), 0);
    });
    return t;
  }, [columnasVisibles, filasFiltradas]);

  // El "Total general" del archivo solo tiene sentido si se ven todas las filas
  const mostrarTotalArchivo = Boolean(filaTotal) && !busqueda.trim() && filas.length === (datos?.totalFilas ?? filas.length);

  /* ---------- Exportar ---------- */
  const exportarCsv = () => {
    const lineas = [columnasVisibles.map((c) => celdaCsv(c.nombre, { tipo: "texto" })).join(";")];
    filasOrdenadas.forEach((f) => lineas.push(columnasVisibles.map((c) => celdaCsv(f[c.id], c)).join(";")));
    const base = String(analisis?.meta?.nombreArchivo || "informe").replace(/\.[^.]+$/, "");
    const fecha = new Date().toISOString().slice(0, 10);
    descargarCsv(`${base}-detalle-${fecha}.csv`, lineas.join("\r\n"));
  };

  if (!datos || columnas.length === 0) return <SinDatos />;

  /* ---------- Render ---------- */
  return (
    <section className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 space-y-3" aria-label="Tabla de datos">
      {/* Grupos de columnas */}
      {grupos.length > 0 && (
        <div>
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <p className="text-xs font-medium text-gray-500">Columnas a mostrar</p>
            <div className="flex items-center gap-3 text-xs">
              <button
                type="button"
                onClick={() => setGruposActivos(new Set(grupos.map((g) => g.id)))}
                className="font-medium text-blue-600 hover:text-blue-800"
              >
                Todas
              </button>
              <button
                type="button"
                onClick={() => setGruposActivos(new Set(gruposBasicos))}
                className="font-medium text-blue-600 hover:text-blue-800"
              >
                Básicas
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {grupos.map((g) => {
              const fijo = g.id === ID_IDENTIFICACION;
              const activo = fijo || gruposActivos.has(g.id);
              return (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => alternarGrupo(g.id)}
                  aria-pressed={activo}
                  disabled={fijo}
                  title={fijo ? "Siempre visible" : `${g.columnas.length} columna(s)`}
                  className={`rounded-full border px-2.5 py-1 text-xs transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    activo
                      ? "bg-blue-600 border-blue-600 text-white"
                      : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                  } ${fijo ? "cursor-default opacity-90" : ""}`}
                >
                  {g.etiqueta}
                  <span className={`ml-1 ${activo ? "text-blue-100" : "text-gray-400"}`}>{g.columnas.length}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Buscador, tamaño de página y exportación */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="relative flex-1 min-w-0">
          <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" aria-hidden="true" />
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por código, descripción, fabricante..."
            aria-label="Buscar en la tabla"
            className="w-full pl-8 pr-8 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {busqueda && (
            <button
              type="button"
              onClick={() => setBusqueda("")}
              aria-label="Borrar búsqueda"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <label className="inline-flex items-center gap-1.5 text-xs text-gray-500">
          Filas por página
          <select
            value={tamano}
            onChange={(e) => setTamano(Number(e.target.value))}
            className="rounded-md border border-gray-200 bg-white px-1.5 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {TAMANOS_PAGINA.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          onClick={exportarCsv}
          disabled={total === 0}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <Download className="w-4 h-4" aria-hidden="true" />
          Exportar CSV
        </button>
      </div>

      <p className="text-xs text-gray-500">
        {total === filas.length
          ? `${total.toLocaleString("es-CO")} filas`
          : `${total.toLocaleString("es-CO")} de ${filas.length.toLocaleString("es-CO")} filas`}
        {datos.totalFilas > filas.length && ` (el archivo tiene ${datos.totalFilas.toLocaleString("es-CO")}; los filtros del dashboard ocultan el resto)`}
        {" · "}
        {columnasVisibles.length} columnas
      </p>

      {/* Tabla */}
      <div className="overflow-auto border border-gray-100 rounded-lg" style={{ maxHeight: "70vh" }}>
        <table className="min-w-full text-sm">
          <thead className="sticky top-0 z-10 bg-gray-50">
            <tr>
              {columnasVisibles.map((c) => {
                const { arriba, abajo } = partesEncabezado(c);
                const activa = orden?.id === c.id;
                const derecha = esNumerica(c);
                return (
                  <th
                    key={c.id}
                    scope="col"
                    aria-sort={activa ? (orden.dir === "asc" ? "ascending" : "descending") : "none"}
                    className={`px-3 py-2 font-semibold text-gray-700 whitespace-nowrap border-b border-gray-200 ${
                      derecha ? "text-right" : "text-left"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => cambiarOrden(c)}
                      className={`inline-flex flex-col ${derecha ? "items-end" : "items-start"} focus:outline-none focus:underline`}
                      title={c.nombre}
                    >
                      {arriba && <span className="text-[10px] font-normal text-gray-400 leading-tight">{arriba}</span>}
                      <span className="inline-flex items-center gap-1 text-xs leading-tight">
                        {abajo}
                        {activa && (orden.dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
                      </span>
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {filasPagina.length === 0 && (
              <tr>
                <td colSpan={columnasVisibles.length} className="px-3 py-10 text-center text-sm text-gray-500">
                  {busqueda ? "Ninguna fila coincide con la búsqueda." : "No hay filas para mostrar con los filtros actuales."}
                </td>
              </tr>
            )}
            {filasPagina.map((f, i) => (
              <tr key={f._fila ?? desde + i} className="hover:bg-gray-50">
                {columnasVisibles.map((c) => {
                  const { texto, clase } = formatearCelda(f[c.id], c);
                  const derecha = esNumerica(c);
                  return (
                    <td
                      key={c.id}
                      className={`px-3 py-1.5 whitespace-nowrap ${derecha ? "text-right tabular-nums" : "max-w-[18rem] truncate"} ${clase}`}
                      title={!derecha && texto.length > 30 ? texto : undefined}
                    >
                      {texto}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>

          {filasFiltradas.length > 0 && (
            <tfoot className="bg-gray-50 border-t-2 border-gray-200">
              <tr className="font-semibold text-gray-800">
                {columnasVisibles.map((c, i) => {
                  if (i === 0) {
                    return (
                      <td key={c.id} className="px-3 py-2 whitespace-nowrap text-xs">
                        Total ({filasFiltradas.length.toLocaleString("es-CO")} filas)
                      </td>
                    );
                  }
                  if (!esNumerica(c)) return <td key={c.id} />;
                  const { texto, clase } = formatearCelda(totales[c.id], c);
                  return (
                    <td key={c.id} className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${clase}`}>
                      {texto}
                    </td>
                  );
                })}
              </tr>
              {mostrarTotalArchivo && (
                <tr className="text-gray-500">
                  {columnasVisibles.map((c, i) => {
                    if (i === 0) {
                      return (
                        <td key={c.id} className="px-3 py-1.5 whitespace-nowrap text-xs">
                          Total general (archivo)
                        </td>
                      );
                    }
                    if (!esNumerica(c)) return <td key={c.id} />;
                    const { texto } = formatearCelda(filaTotal[c.id], c);
                    return (
                      <td key={c.id} className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">
                        {texto}
                      </td>
                    );
                  })}
                </tr>
              )}
            </tfoot>
          )}
        </table>
      </div>

      {/* Paginación */}
      {total > 0 && (
        <div className="flex items-center justify-between gap-2 text-xs text-gray-600">
          <span>
            Mostrando {(desde + 1).toLocaleString("es-CO")}–{Math.min(desde + tamano, total).toLocaleString("es-CO")} de{" "}
            {total.toLocaleString("es-CO")}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPagina(paginaActual - 1)}
              disabled={paginaActual <= 1}
              aria-label="Página anterior"
              className="p-1.5 rounded-md border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-2 tabular-nums">
              Página {paginaActual} de {totalPaginas}
            </span>
            <button
              type="button"
              onClick={() => setPagina(paginaActual + 1)}
              disabled={paginaActual >= totalPaginas}
              aria-label="Página siguiente"
              className="p-1.5 rounded-md border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

export default TablaDetalle;